import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Award, Clock, BookOpen, Zap } from 'lucide-react';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import LoadingState from '../../components/ui/LoadingState';
import { getTrainingCourses, awardXp } from '../../services/rewardService';
import { fetchMicroLessons, fetchMicroLessonById } from '../../services/microLessonsService';
import { useAuth } from '../../contexts/AuthContext';

const TrainingPage = () => {
  const { courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [xpAwarded, setXpAwarded] = useState(null);
  const [awarding, setAwarding] = useState(false);
  const [error, setError] = useState(null);

  const fromAnalysis = location.state?.fromAnalysis || false;
  const bonusEligible = location.state?.bonus || false;
  const weakestAreas = location.state?.weakestAreas || [];
  const userLevel = profile?.level || 1;
  const [completionMap, setCompletionMap] = useState({});
  const [coreCompleted, setCoreCompleted] = useState(false);

  const CORE_COURSES = ['discovery-mastery', 'objection-mastery', 'closing-mastery'];

  const checkCoreCompleted = (map) => CORE_COURSES.every((c) => map?.[c]);

  const getStorageKey = (userId) => `training_progress_${userId}`;

  const loadCompletionMap = (userId) => {
    if (typeof window === 'undefined' || !userId) return {};
    try {
      const raw = localStorage.getItem(getStorageKey(userId));
      return raw ? JSON.parse(raw) : {};
    } catch (err) {
      console.warn('Could not read training progress from storage', err);
      return {};
    }
  };

  const persistCompletion = (userId, map) => {
    if (typeof window === 'undefined' || !userId) return;
    try {
      localStorage.setItem(getStorageKey(userId), JSON.stringify(map));
    } catch (err) {
      console.warn('Could not write training progress to storage', err);
    }
  };

  useEffect(() => {
    loadTrainingContent();
    const map = loadCompletionMap(user?.id);
    setCompletionMap(map);
    setCoreCompleted(checkCoreCompleted(map));
    setCompleted(!!map?.[courseId]);
  }, [courseId, user?.id]);

  const loadTrainingContent = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get course info from reward service
      const allCourses = getTrainingCourses();
      const courseInfo = allCourses[courseId];
      
      if (!courseInfo) {
        throw new Error('Training course not found');
      }

      setCourse(courseInfo);

      // Try to find a related micro-lesson
      try {
        const lessons = await fetchMicroLessons();
        // Find lesson that matches the course focus area
        const relatedLesson = lessons.find(l => 
          l?.tags?.includes(courseInfo?.tags?.[0]) ||
          l?.title?.toLowerCase()?.includes(courseInfo?.name?.toLowerCase()?.split(' ')?.[0])
        );
        
        if (relatedLesson) {
          setLesson(relatedLesson);
        }
      } catch (err) {
        console.warn('Could not load related lesson:', err);
      }
    } catch (err) {
      setError(err.message || 'Failed to load training');
      console.error('Error loading training:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTraining = async () => {
    if (!user || !course) return;
    if (isLocked) {
      setError(lockMessage || 'This module is locked.');
      return;
    }

    try {
      setAwarding(true);
      
      // Award XP for completing training
      const result = await awardXp(
        user.id,
        course.points,
        `training_completion_${courseId}`
      );

      const nextMap = { ...completionMap, [courseId]: true };
      setCompletionMap(nextMap);
      persistCompletion(user.id, nextMap);
      setCoreCompleted(checkCoreCompleted(nextMap));

      setXpAwarded(result);
      setCompleted(true);

    } catch (err) {
      setError('Error awarding XP. Please try again.');
      console.error('Error awarding XP:', err);
    } finally {
      setAwarding(false);
    }
  };

  const handleBackClick = () => {
    if (fromAnalysis) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const isLevelLocked = course?.unlockLevel && userLevel < course.unlockLevel;
  const requiresCore = course?.tier === 'advanced';
  const isCoreLocked = requiresCore && !coreCompleted;
  const isLocked = isLevelLocked || isCoreLocked;
  const hasAwardDetails = Boolean(xpAwarded);

  const lockMessage = isLocked
    ? isLevelLocked && isCoreLocked
      ? `Reach Level ${course.unlockLevel} and finish the core trainings to unlock this advanced module.`
      : isLevelLocked
        ? `Unlocks at Level ${course.unlockLevel}.`
        : 'Complete the core trainings to unlock this advanced module.'
    : '';

  if (loading) {
    return <LoadingState message={`Loading ${courseId?.replace('-', ' ')}...`} />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50">
        <NavigationBar />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <h2 className="text-2xl font-bold text-red-900 mb-2">Unable to Load Training</h2>
            <p className="text-red-700 mb-6">{error}</p>
            <Button onClick={handleBackClick} variant="outline">
              Return to Previous Page
            </Button>
          </div>
        </div></div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <NavigationBar />
        <div className="max-w-4xl mx-auto px-4 py-12">
          <p className="text-center text-gray-600">Training course not found</p>
        </div></div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <NavigationBar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={handleBackClick}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        {!completed ? (
          <>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-6 h-6 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Training Module</span>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{course.name}</h1>
              <p className="text-lg text-gray-600">{course.description}</p>
              {isLocked && (
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <Award className="w-4 h-4 text-amber-600" />
                  {lockMessage}
                </div>
              )}
            </div>

            {/* Course Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-600">Duration</p>
                    <p className="text-xl font-bold text-gray-900">{course.duration}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <Award className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="text-sm text-gray-600">XP Reward</p>
                    <p className="text-xl font-bold text-gray-900">+{course.points} XP</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3">
                  <Zap className="w-6 h-6 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-600">Focus Area</p>
                    <p className="text-sm font-bold text-gray-900 capitalize">
                      {course.focus?.replace('_', ' ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bonus Badge */}
            {bonusEligible && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-6 mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-amber-600" />
                  <p className="font-bold text-amber-900">🎉 Bonus Offer Active!</p>
                </div>
                <p className="text-sm text-amber-800">
                  You unlocked this training with a high score. Complete it now to earn your <span className="font-bold">+{course.points} bonus XP</span>!
                </p>
              </div>
            )}

            {weakestAreas?.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
                <p className="text-sm font-semibold text-blue-900 mb-2">Your current weakest areas</p>
                <div className="flex flex-wrap gap-3">
                  {weakestAreas.slice(0, 3).map((area, idx) => (
                    <span key={idx} className="px-3 py-1 rounded-full bg-white border border-blue-200 text-xs font-medium text-blue-800">
                      {area.label}: {area.score}/100
                    </span>
                  ))}
                </div>
                <p className="text-xs text-blue-700 mt-2">Revisit these while you work through this module to push scores higher.</p>
              </div>
            )}

            {/* Content */}
            <div className="bg-white rounded-lg p-8 border border-gray-200 shadow-sm mb-8">
              {lesson ? (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{lesson.title}</h2>
                  
                  <div className="prose prose-sm max-w-none mb-6">
                    <p className="text-gray-700 mb-4">{lesson.lessonText}</p>
                  </div>

                  {lesson.keySteps && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Steps</h3>
                      <ul className="space-y-2">
                        {lesson.keySteps.split('\n').filter(step => step.trim()).map((step, idx) => (
                          <li key={idx} className="flex gap-3">
                            <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-600 rounded-full text-xs font-bold flex-shrink-0">
                              {idx + 1}
                            </span>
                            <span className="text-gray-700">{step.replace(/^\d+\.\s*/, '')}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {lesson.exampleDialogue && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">Example</h3>
                      <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                        <p className="text-gray-700 italic">{lesson.exampleDialogue}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{course.name}</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    {course.description}
                  </p>
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800">
                      📚 <strong>Course Content:</strong> This training module covers advanced techniques to help you master {course.focus?.replace(/_/g, ' ')}.
                    </p>
                  </div>
                  <p className="text-gray-600">
                    Click "Complete Training" below to finish and claim your {course.points} XP reward.
                  </p>
                </div>
              )}
            </div>

            {/* Call to Action */}
            <div className="flex gap-4">
              <Button 
                onClick={handleBackClick} 
                variant="outline"
                className="flex-1"
              >
                Skip for Now
              </Button>
              <Button 
                onClick={handleCompleteTraining}
                disabled={awarding || isLocked}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLocked ? 'Locked until requirements met' : awarding ? 'Completing...' : `Complete Training & Earn +${course.points} XP`}
              </Button>
            </div>
            {isLocked && (
              <p className="text-xs text-amber-800 mt-2">{lockMessage}</p>
            )}
          </>
        ) : (
          // Completion Screen
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Training Complete! 🎉</h2>
            
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-8 border border-green-200 mb-8">
                {hasAwardDetails ? (
                  <div className="space-y-4">
                    {xpAwarded?.leveledUp && (
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Award className="w-6 h-6 text-amber-600" />
                        <div>
                          <p className="text-lg font-bold text-green-900">Level Up! 🚀</p>
                          <p className="text-sm text-green-700">You're now {xpAwarded.newSkillTier} (Level {xpAwarded.newLevel})</p>
                        </div>
                      </div>
                    )}
                  
                    <div>
                      <p className="text-sm text-gray-600 mb-1">XP Earned</p>
                      <p className="text-4xl font-bold text-green-600">+{xpAwarded?.xpAwarded} XP</p>
                    </div>

                    <div className="pt-4 border-t border-green-300">
                      <p className="text-sm text-green-800 mb-1">New Total</p>
                      <p className="text-2xl font-bold text-green-900">{xpAwarded?.newXp} XP</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-center">
                    <p className="text-sm text-green-900 font-semibold">Already completed</p>
                    <p className="text-sm text-green-800">This module is marked complete for unlocking advanced trainings.</p>
                  </div>
                )}
            </div>

            <p className="text-lg text-gray-600 mb-8">
              Great work! You've completed this training module and earned your XP reward. 
              Keep practicing to master your sales skills!
            </p>

            <Button 
              onClick={() => navigate('/app/conversation-analyzer')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Do Another Analysis
            </Button>
          </div>
        )}
      </div></div>
  );
};

export default TrainingPage;
